import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  /** Set by AuthProvider; called when the server reports an expired/revoked session. */
  onSessionExpired = null;

  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      // An authenticated request rejected with 401 means the session expired or was revoked.
      if (res.status === 401 && token && !endpoint.startsWith('/auth/login') && !endpoint.startsWith('/auth/mfa')) {
        this.onSessionExpired?.();
      }
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  selfRegister(payload) {
    return this.request('/auth/self-register', { method: 'POST', body: payload });
  }

  getInvitation(token) {
    return this.request(`/auth/invitations/${encodeURIComponent(token)}`);
  }

  verifyEmail({ token }) {
    return this.request('/auth/verify-email', { method: 'POST', body: { token } });
  }

  forgotPassword({ email }) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  resetPassword({ token, password }) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  verifyMfa({ mfaToken, code }) {
    return this.request('/auth/mfa/verify', { method: 'POST', body: { mfaToken, code } });
  }

  requestMagicLink({ email }) {
    return this.request('/auth/magic-link', { method: 'POST', body: { email } });
  }

  verifyMagicLink({ token }) {
    return this.request('/auth/magic-link/verify', { method: 'POST', body: { token } });
  }

  logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  signOut() {
    return this.request('/auth/sign-out', { method: 'POST' });
  }

  signOutEverywhere() {
    return this.request('/auth/sign-out-everywhere', { method: 'POST' });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Role permissions & security policies ----
  getPermissionCatalog() {
    return this.request('/role-permissions');
  }

  getRoleGrants(role) {
    return this.request(`/role-permissions/roles/${encodeURIComponent(role)}`);
  }

  updateRoleGrants(role, changes) {
    return this.request(`/role-permissions/roles/${encodeURIComponent(role)}`, { method: 'PUT', body: { changes } });
  }

  getSecurityPolicies() {
    return this.request('/role-permissions/policies');
  }

  updateSecurityPolicies(policies) {
    return this.request('/role-permissions/policies', { method: 'PUT', body: policies });
  }

  // ---- Tenant users & access ----
  listTenantUsers({ limit = 10, offset = 0, search, role, status } = {}) {
    const q = new URLSearchParams({ limit, offset });
    if (search) q.set('search', search);
    if (role) q.set('role', role);
    if (status) q.set('status', status);
    return this.request(`/tenant-users?${q.toString()}`);
  }

  inviteTenantUser({ fullName, email, role }) {
    return this.request('/tenant-users', { method: 'POST', body: { fullName, email, role } });
  }

  resendTenantInvite(id) {
    return this.request(`/tenant-users/${id}/resend-invite`, { method: 'POST' });
  }

  updateTenantUserRoles(id, roles) {
    return this.request(`/tenant-users/${id}/roles`, { method: 'PUT', body: { roles } });
  }

  suspendTenantUser(id) {
    return this.request(`/tenant-users/${id}/suspend`, { method: 'POST' });
  }

  reactivateTenantUser(id) {
    return this.request(`/tenant-users/${id}/reactivate`, { method: 'POST' });
  }

  // ---- Spatial locations (GV-11) ----
  getLocationTree({ search, tier } = {}) {
    const q = new URLSearchParams();
    if (search) q.set('search', search);
    if (tier && tier !== 'ALL') q.set('tier', tier);
    const qs = q.toString();
    return this.request(`/locations/tree${qs ? `?${qs}` : ''}`);
  }

  getLocation(id) {
    return this.request(`/locations/${id}`);
  }

  createLocation(body) {
    return this.request('/locations', { method: 'POST', body });
  }

  updateLocation(id, body) {
    return this.request(`/locations/${id}`, { method: 'PATCH', body });
  }

  moveLocation(id, parentId) {
    return this.request(`/locations/${id}/move`, { method: 'POST', body: { parentId } });
  }

  archiveLocation(id) {
    return this.request(`/locations/${id}/archive`, { method: 'POST' });
  }

  rotateLocationQr(id) {
    return this.request(`/locations/${id}/rotate-qr`, { method: 'POST' });
  }

  importLocations({ format, content, dryRun }) {
    return this.request('/locations/import', { method: 'POST', body: { format, content, dryRun } });
  }

  // ---- QR placards (GV-12) ----
  listQrPlacards({ scopeId, status, search, page = 1, pageSize = 200 } = {}) {
    const q = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (scopeId && scopeId !== 'ALL') q.set('scopeId', scopeId);
    if (status && status !== 'ALL') q.set('status', status);
    if (search) q.set('search', search);
    return this.request(`/qr/placards?${q.toString()}`);
  }

  generateQrTokens(locationIds, regenerate = false) {
    return this.request('/qr/generate', { method: 'POST', body: { locationIds, regenerate } });
  }

  revokeQrTokens(locationIds) {
    return this.request('/qr/revoke', { method: 'POST', body: { locationIds } });
  }

  regenerateQrTokens(locationIds) {
    return this.request('/qr/regenerate', { method: 'POST', body: { locationIds } });
  }

  getQrSettings() {
    return this.request('/qr/settings');
  }

  resolveQrToken(token) {
    return this.request(`/qr/resolve/${encodeURIComponent(token)}`);
  }

  // ---- QR defect intake (public) + triage queue ----
  getIntakeContext(token) {
    return this.request(`/intake/context/${encodeURIComponent(token)}`);
  }

  /** Submits the defect as multipart (photos are binary files, never base64 in JSON). */
  async submitDefectTicket(formData) {
    const res = await fetch(`${API_BASE_URL}/intake/tickets`, { method: 'POST', body: formData });
    const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
    if (!res.ok) {
      const detail = payload?.error?.details?.[0]?.message;
      const error = new Error(detail || payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return payload?.data ?? payload;
  }

  trackTicket(ticketNumber, key) {
    return this.request(`/intake/track/${encodeURIComponent(ticketNumber)}?key=${encodeURIComponent(key)}`);
  }

  listTickets({ status, category, search, page = 1, pageSize = 25 } = {}) {
    const q = new URLSearchParams({ page, pageSize });
    if (status) q.set('status', status);
    if (category) q.set('category', category);
    if (search) q.set('search', search);
    return this.request(`/tickets?${q.toString()}`);
  }

  /** Downloads the printable PDF label sheet; resolves to { blob, labels }. */
  async downloadQrPdf({ locationIds, template, brandingHeader, instruction }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/qr/pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ locationIds, template, brandingHeader, instruction }),
    });
    if (!res.ok) {
      const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return { blob: await res.blob(), labels: Number(res.headers.get('X-Label-Count') || 0) };
  }

  // ---- Resident portal (authenticated Raiser) ----
  getResidentPortal() {
    return this.request('/resident/portal');
  }

  listResidentTickets({ activeOnly = false, page = 1, pageSize = 20 } = {}) {
    const q = new URLSearchParams({ page, pageSize });
    if (activeOnly) q.set('activeOnly', 'true');
    return this.request(`/resident/tickets?${q.toString()}`);
  }

  /** Multipart submit (photos are binary files, never base64 in JSON); the request helper keeps the Bearer token. */
  async submitResidentTicket(formData) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/resident/tickets`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
    if (!res.ok) {
      const detail = payload?.error?.details?.[0]?.message;
      const error = new Error(detail || payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      if (res.status === 401 && token) this.onSessionExpired?.();
      throw error;
    }
    return payload?.data ?? payload;
  }

  rateResidentTicket(id, { rating, comment }) {
    return this.request(`/resident/tickets/${id}/rating`, { method: 'POST', body: { rating, comment } });
  }

  // ---- Chargeable repairs: the resident's side (Raiser) ----
  listMyResidentCharges() {
    return this.request('/resident/charges');
  }

  acceptResidentCharge(id) {
    return this.request(`/resident/charges/${id}/accept`, { method: 'POST' });
  }

  declineResidentCharge(id, reason) {
    return this.request(`/resident/charges/${id}/decline`, { method: 'POST', body: reason ? { reason } : {} });
  }

  /** The resident's own invoice for a finalised charge; resolves to a PDF Blob. */
  downloadMyResidentChargeInvoice(id) {
    return this.downloadBillingBlob(`/resident/charges/${id}/invoice`);
  }

  // ---- Chargeable repairs: staff (Supervisor / Manager / Customer Admin raise; Finance takes payment; Service Desk reads) ----
  listResidentCharges({ status = 'ALL', q = '', page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ status, page, pageSize });
    if (q) params.set('q', q);
    return this.request(`/resident-charges?${params.toString()}`);
  }

  getResidentChargeSettings() {
    return this.request('/resident-charges/settings');
  }

  updateResidentChargeSettings(channel) {
    return this.request('/resident-charges/settings', { method: 'PUT', body: { channel } });
  }

  createResidentCharge({ ticketId, reasonCode, reasonNote, estimateCents }) {
    return this.request('/resident-charges', { method: 'POST', body: { ticketId, reasonCode, ...(reasonNote ? { reasonNote } : {}), estimateCents } });
  }

  getResidentCharge(id) {
    return this.request(`/resident-charges/${id}`);
  }

  reviseResidentChargeEstimate(id, { estimateCents, reasonNote }) {
    return this.request(`/resident-charges/${id}/estimate`, { method: 'PATCH', body: { estimateCents, ...(reasonNote ? { reasonNote } : {}) } });
  }

  cancelResidentCharge(id, reason) {
    return this.request(`/resident-charges/${id}/cancel`, { method: 'POST', body: { reason } });
  }

  overrideResidentCharge(id, reason) {
    return this.request(`/resident-charges/${id}/override`, { method: 'POST', body: { reason } });
  }

  /** Resolves to the charge plus `needsAcceptance` (true when the amount went back to the resident instead of being invoiced). */
  finaliseResidentCharge(id, finalCents) {
    return this.request(`/resident-charges/${id}/finalise`, { method: 'POST', body: { finalCents } });
  }

  recordResidentChargePayment(id, { reference, paidOn }) {
    return this.request(`/resident-charges/${id}/record-payment`, { method: 'POST', body: { reference, ...(paidOn ? { paidOn } : {}) } });
  }

  /** Invoice PDF of a finalised charge; resolves to a Blob. */
  downloadResidentChargeInvoice(id) {
    return this.downloadBillingBlob(`/resident-charges/${id}/invoice`);
  }

  // ---- Inspections (Supervisor / Manager / Customer Admin schedule and review; they and the assigned technician walk) ----
  listInspections({ status = 'OPEN', q = '', propertyId = '', page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ status, page, pageSize });
    if (q) params.set('q', q);
    if (propertyId) params.set('propertyId', propertyId);
    return this.request(`/inspections?${params.toString()}`);
  }

  getInspectionOptions() {
    return this.request('/inspections/options');
  }

  scheduleInspections({ templateId, locationId, assignedTo, scheduledFor }) {
    return this.request('/inspections/schedule', { method: 'POST', body: { templateId, locationId, assignedTo: assignedTo || null, scheduledFor: scheduledFor || null } });
  }

  getInspection(id) {
    return this.request(`/inspections/${id}`);
  }

  startInspection(id) {
    return this.request(`/inspections/${id}/start`, { method: 'POST' });
  }

  /** Records one item's answer: { result, reading, choice, note }. Sending all of them empty clears the answer. */
  answerInspectionItem(id, itemId, { result = null, reading = null, choice = null, note = '' }) {
    return this.request(`/inspections/${id}/items/${itemId}`, { method: 'PATCH', body: { result, reading, choice, note } });
  }

  addInspectionSnag(id, { name, trade, severity, note }) {
    return this.request(`/inspections/${id}/items`, { method: 'POST', body: { name, trade, severity, note } });
  }

  removeInspectionSnag(id, itemId) {
    return this.request(`/inspections/${id}/items/${itemId}`, { method: 'DELETE' });
  }

  async addInspectionPhoto(id, itemId, blob) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/inspections/${id}/items/${itemId}/photos`, {
      method: 'POST',
      headers: { 'Content-Type': blob.type || 'image/jpeg', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: blob,
    });
    const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
    if (!res.ok) throw new Error(payload?.error?.message || `Photo upload failed (${res.status})`);
    return payload?.data;
  }

  removeInspectionPhoto(id, photoId) {
    return this.request(`/inspections/${id}/photos/${photoId}`, { method: 'DELETE' });
  }

  /** contentUrl comes from the inspection's item photos; resolves to an image Blob. */
  fetchInspectionPhotoBlob(contentUrl) {
    return this.downloadBillingBlob(contentUrl);
  }

  completeInspection(id, { ownerName, signature, ownerAbsentReason, notes }) {
    return this.request(`/inspections/${id}/complete`, { method: 'POST', body: { ownerName: ownerName || null, signature: signature || null, ownerAbsentReason: ownerAbsentReason || null, notes: notes || null } });
  }

  cancelInspection(id, reason) {
    return this.request(`/inspections/${id}/cancel`, { method: 'POST', body: { reason } });
  }

  assignInspection(id, { assignedTo, scheduledFor }) {
    return this.request(`/inspections/${id}/assign`, { method: 'PATCH', body: { assignedTo: assignedTo || null, scheduledFor: scheduledFor || null } });
  }

  reinspect(id) {
    return this.request(`/inspections/${id}/reinspect`, { method: 'POST', body: {} });
  }

  /** Printable inspection report; resolves to a PDF Blob. */
  downloadInspectionReport(id) {
    return this.downloadBillingBlob(`/inspections/${id}/report`);
  }

  // ---- Resident directory (Customer Admin / Manager manage; Supervisor / Service Desk Agent read) ----
  listResidents({ status = 'ALL', source = 'ALL', account = 'ALL', q = '', locationId, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ status, source, account, page: String(page), pageSize: String(pageSize) });
    if (q) params.set('q', q);
    if (locationId) params.set('locationId', locationId);
    return this.request(`/residents?${params.toString()}`);
  }

  searchResidentUnits(q = '', limit = 50) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (q) params.set('q', q);
    return this.request(`/residents/units?${params.toString()}`);
  }

  createResident(payload) {
    return this.request('/residents', { method: 'POST', body: payload });
  }

  updateResident(id, payload) {
    return this.request(`/residents/${id}`, { method: 'PATCH', body: payload });
  }

  approveResident(id, { locationId, relationship, note }) {
    return this.request(`/residents/${id}/approve`, { method: 'POST', body: { locationId, ...(relationship ? { relationship } : {}), ...(note ? { note } : {}) } });
  }

  rejectResident(id, reason) {
    return this.request(`/residents/${id}/reject`, { method: 'POST', body: { reason } });
  }

  moveOutResident(id, { moveOutDate, note } = {}) {
    return this.request(`/residents/${id}/move-out`, { method: 'POST', body: { ...(moveOutDate ? { moveOutDate } : {}), ...(note ? { note } : {}) } });
  }

  inviteResident(id) {
    return this.request(`/residents/${id}/invite`, { method: 'POST' });
  }

  importResidents(residents) {
    return this.request('/residents/import', { method: 'POST', body: { residents } });
  }

  getResidentSettings() {
    return this.request('/residents/settings');
  }

  rotateCommunityCode() {
    return this.request('/residents/settings/community-code/rotate', { method: 'POST' });
  }

  createResidentApiKey(name) {
    return this.request('/residents/settings/api-keys', { method: 'POST', body: { name } });
  }

  revokeResidentApiKey(id) {
    return this.request(`/residents/settings/api-keys/${id}`, { method: 'DELETE' });
  }

  // ---- Service desk console (Service Desk Agent and supervising roles) ----
  getServiceDeskSummary() {
    return this.request('/servicedesk/summary');
  }

  getServiceDeskLocations() {
    return this.request('/servicedesk/locations');
  }

  searchServiceDeskContacts(q = '') {
    return this.request(`/servicedesk/contacts?q=${encodeURIComponent(q)}`);
  }

  createServiceDeskContact(payload) {
    return this.request('/servicedesk/contacts', { method: 'POST', body: payload });
  }

  logServiceDeskTicket(payload) {
    return this.request('/servicedesk/tickets', { method: 'POST', body: payload });
  }

  // ---- Supervisor triage board (Supervisor and supervising roles) ----
  getTriageBoard({ property, trade, tier, q, limit, asOf } = {}) {
    const params = new URLSearchParams();
    if (property && property !== 'ALL') params.set('property', property);
    if (trade && trade !== 'ALL') params.set('trade', trade);
    if (tier && tier !== 'ALL') params.set('tier', tier);
    if (q) params.set('q', q);
    if (limit) params.set('limit', String(limit));
    if (asOf) params.set('asOf', asOf);
    const qs = params.toString();
    return this.request(`/triage/board${qs ? `?${qs}` : ''}`);
  }

  getTriageTicket(id, asOf) {
    return this.request(`/triage/tickets/${id}${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`);
  }

  adjustTriagePriority(id, { severity, urgency, justification }) {
    return this.request(`/triage/tickets/${id}/priority`, { method: 'PATCH', body: { severity, urgency, justification } });
  }

  moveTriageTicket(id, column) {
    return this.request(`/triage/tickets/${id}/move`, { method: 'PATCH', body: { column } });
  }

  setTriageTrade(id, category) {
    return this.request(`/triage/tickets/${id}/trade`, { method: 'PATCH', body: { category } });
  }

  closeTriageTicket(id) {
    return this.request(`/triage/tickets/${id}/close`, { method: 'POST' });
  }

  commentTriageTicket(id, message) {
    return this.request(`/triage/tickets/${id}/comments`, { method: 'POST', body: { message } });
  }

  getTriageDuplicateCandidate(id) {
    return this.request(`/triage/tickets/${id}/duplicate-candidate`);
  }

  linkTriageDuplicate(id, masterTicketId) {
    return this.request(`/triage/tickets/${id}/link-duplicate`, { method: 'POST', body: { masterTicketId } });
  }

  /** Streams an authorized ticket photo; resolves to a Blob. */
  async getTicketPhoto(ticketId, attachmentId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Photo unavailable (${res.status})`);
    return res.blob();
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- Work order packaging & dispatch board (Supervisor and supervising roles) ----
  getWorkOrders({ q, trade, status, type, page, pageSize } = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (trade && trade !== 'ALL') params.set('trade', trade);
    if (status && status !== 'ALL') params.set('status', status);
    if (type && type !== 'ALL') params.set('type', type);
    if (page) params.set('page', String(page));
    if (pageSize) params.set('pageSize', String(pageSize));
    const qs = params.toString();
    return this.request(`/workorders${qs ? `?${qs}` : ''}`);
  }

  getPackageableTickets() {
    return this.request('/workorders/packageable-tickets');
  }

  getDispatchRecommendations(workOrderId) {
    return this.request(`/workorders/${encodeURIComponent(workOrderId)}/recommendations`);
  }

  getDispatchRoster(trade) {
    return this.request(`/workorders/roster${trade ? `?trade=${encodeURIComponent(trade)}` : ''}`);
  }

  getWorkOrderChains() {
    return this.request('/workorders/chains');
  }

  createWorkOrder(payload) {
    return this.request('/workorders', { method: 'POST', body: payload });
  }

  dispatchWorkOrder(id, payload) {
    return this.request(`/workorders/${id}/dispatch`, { method: 'POST', body: payload });
  }

  progressWorkOrder(id, action) {
    return this.request(`/workorders/${id}/progress`, { method: 'POST', body: { action } });
  }

  // ---- Provider profile & accreditation management (Provider Admin; customer admin verification) ----
  getProviderProfile() {
    return this.request('/provider-compliance/me');
  }

  refreshProviderCompliance() {
    return this.request('/provider-compliance/me/refresh', { method: 'POST' });
  }

  updateProviderCompany(payload) {
    return this.request('/provider-compliance/me/company', { method: 'PUT', body: payload });
  }

  addProviderTechnician(payload) {
    return this.request('/provider-compliance/me/technicians', { method: 'POST', body: payload });
  }

  /** Multipart certificate upload (the file is stored in backend storage, never in a database column). */
  async uploadProviderDocument(formData) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/provider-compliance/me/documents`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
    if (!res.ok) {
      const detail = payload?.error?.details?.[0]?.message;
      const error = new Error(detail || payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      if (res.status === 401 && token) this.onSessionExpired?.();
      throw error;
    }
    return payload?.data ?? payload;
  }

  /** Fetches a certificate through the authorized streaming endpoint; resolves to a Blob. */
  async getProviderDocumentFile(docId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/provider-compliance/me/documents/${docId}/file`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Document unavailable (${res.status})`);
    return res.blob();
  }

  listComplianceProviders() {
    return this.request('/provider-compliance/providers');
  }

  getProviderDocuments(providerId) {
    return this.request(`/provider-compliance/providers/${providerId}/documents`);
  }

  reviewProviderDocument(providerId, docId, payload) {
    return this.request(`/provider-compliance/providers/${providerId}/documents/${docId}/review`, { method: 'POST', body: payload });
  }

  // ---- Trade categories & contracted rate cards (Provider Admin configures; customer admin approves) ----
  listRateCards({ status = 'ALL', trade = 'ALL', q = '', page = 1, pageSize = 50 } = {}) {
    const params = new URLSearchParams({ status, trade, page: String(page), pageSize: String(pageSize) });
    if (q) params.set('q', q);
    return this.request(`/rate-cards?${params.toString()}`);
  }

  getRateCardAudit() {
    return this.request('/rate-cards/audit');
  }

  submitRateCard(payload) {
    return this.request('/rate-cards', { method: 'POST', body: payload });
  }

  reviewRateCard(id, payload) {
    return this.request(`/rate-cards/${id}/review`, { method: 'POST', body: payload });
  }

  createRateQuote(payload) {
    return this.request('/rate-cards/quotes', { method: 'POST', body: payload });
  }

  // ---- Contractor quotation builder (Provider Admin) ----
  listQuotableWorkOrders({ q = '', page = 1, pageSize = 50 } = {}) {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q) params.set('q', q);
    return this.request(`/quotations/work-orders?${params.toString()}`);
  }

  getQuotableWorkOrder(id) {
    return this.request(`/quotations/work-orders/${id}`);
  }

  saveContractorQuotation(payload) {
    return this.request('/quotations', { method: 'POST', body: payload });
  }

  // ---- Multi-tier quotation approvals (GV-06) ----
  getApprovalQueue({ view = 'PENDING', tier = '', q = '', page = 1, pageSize = 100 } = {}) {
    const params = new URLSearchParams({ view, page: String(page), pageSize: String(pageSize) });
    if (tier) params.set('tier', tier);
    if (q) params.set('q', q);
    return this.request(`/approvals/queue?${params.toString()}`);
  }

  getApprovalQuotation(id) {
    return this.request(`/approvals/quotations/${id}`);
  }

  decideApprovalQuotation(id, payload) {
    return this.request(`/approvals/quotations/${id}/decision`, { method: 'POST', body: payload });
  }

  // ---- AP vouchers & ERP export ----
  getApVouchers({ status = 'ALL', expenseType = 'ALL', page = 1, pageSize = 200 } = {}) {
    const params = new URLSearchParams({ status, expenseType, page: String(page), pageSize: String(pageSize) });
    return this.request(`/ap-vouchers?${params.toString()}`);
  }

  previewApExport(payload) {
    return this.request('/ap-vouchers/export/preview', { method: 'POST', body: payload });
  }

  exportApVouchers(payload) {
    return this.request('/ap-vouchers/export', { method: 'POST', body: payload });
  }

  markApVouchersPaid(voucherIds) {
    return this.request('/ap-vouchers/mark-paid', { method: 'POST', body: { voucherIds } });
  }

  // ---- Completion evidence & dual sign-off ----
  getSignoffContext(workOrderId) {
    return this.request(`/signoff/context${workOrderId ? `?workOrderId=${encodeURIComponent(workOrderId)}` : ''}`);
  }

  submitSignoff(workOrderId, payload) {
    return this.request(`/signoff/work-orders/${encodeURIComponent(workOrderId)}/submit`, { method: 'POST', body: payload });
  }

  // ---- Resident (Raiser) verification of completed repairs ----
  getVerificationContext(workOrderId) {
    return this.request(`/verification/context${workOrderId ? `?workOrderId=${encodeURIComponent(workOrderId)}` : ''}`);
  }

  submitVerificationRating(workOrderId, payload) {
    return this.request(`/verification/work-orders/${encodeURIComponent(workOrderId)}/rating`, { method: 'POST', body: payload });
  }

  async uploadReworkPhoto(workOrderId, file) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/verification/work-orders/${encodeURIComponent(workOrderId)}/rework-photo`, {
      method: 'POST',
      headers: { 'Content-Type': file.type, 'X-Captured-At': new Date().toISOString(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(payload?.error?.message || `Photo upload failed (${res.status})`);
    return payload?.data?.photo ?? payload?.photo;
  }

  // ---- Technician execution (offline-first) ----
  getExecutionJobs({ page = 1, pageSize = 50 } = {}) {
    return this.request(`/execution/jobs?page=${page}&pageSize=${pageSize}`);
  }

  getHandoverKey() {
    return this.request('/execution/handover-key');
  }

  replaySync(mutations) {
    return this.request('/sync/replay', { method: 'POST', body: { mutations } });
  }

  /** Uploads an After photo as the raw image body (idempotent by the mutation ULID). */
  async uploadExecutionPhoto(workOrderId, blob, { mutationId, capturedAt, gps, sha256 }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/execution/jobs/${workOrderId}/photos`, {
      method: 'POST',
      headers: {
        'Content-Type': blob.type || 'image/jpeg',
        'X-Mutation-Id': mutationId,
        'X-Captured-At': capturedAt,
        'X-Photo-Kind': 'AFTER',
        ...(gps ? { 'X-GPS': gps } : {}),
        ...(sha256 ? { 'X-SHA256': sha256 } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: blob,
    });
    const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
    if (!res.ok) {
      const error = new Error(payload?.error?.message || `Photo upload failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return payload?.data;
  }

  // ---- Inspection checklist templates & task items (Supervisor / Customer Admin) ----
  listInspectionTemplates({ q = '', trade = 'ALL', propertyType = 'ALL', status = 'ALL', page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ trade, propertyType, status, page: String(page), pageSize: String(pageSize) });
    if (q) params.set('q', q);
    return this.request(`/inspection-templates?${params.toString()}`);
  }

  getInspectionTemplate(id) {
    return this.request(`/inspection-templates/${id}`);
  }

  createInspectionTemplate(payload) {
    return this.request('/inspection-templates', { method: 'POST', body: payload });
  }

  updateInspectionTemplate(id, payload) {
    return this.request(`/inspection-templates/${id}`, { method: 'PUT', body: payload });
  }

  setInspectionTemplateStatus(id, isActive) {
    return this.request(`/inspection-templates/${id}/status`, { method: 'PATCH', body: { isActive } });
  }

  duplicateInspectionTemplate(id) {
    return this.request(`/inspection-templates/${id}/duplicate`, { method: 'POST' });
  }

  deleteInspectionTemplate(id) {
    return this.request(`/inspection-templates/${id}`, { method: 'DELETE' });
  }

  // ---- Operational SLA & performance analytics (Manager / Supervisor / Admin) ----
  slaDashboardParams(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'ALL') params.set(k, String(v));
    });
    return params.toString();
  }

  getSlaDashboard(filters = {}) {
    return this.request(`/analytics/sla-dashboard?${this.slaDashboardParams(filters)}`);
  }

  async exportSlaDashboardCsv(filters = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/analytics/sla-dashboard/export.csv?${this.slaDashboardParams(filters)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
      throw new Error(payload?.error?.message || `Export failed (${res.status})`);
    }
    return res.blob();
  }

  // ---- Provider benchmark scorecards (GV-10; Manager / Supervisor / Admin) ----
  scorecardParams(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'ALL') params.set(k, String(v));
    });
    return params.toString();
  }

  getProviderScorecards(filters = {}) {
    return this.request(`/provider-scorecards?${this.scorecardParams(filters)}`);
  }

  getProviderScorecard(id) {
    return this.request(`/provider-scorecards/${id}`);
  }

  previewProviderScore(metrics) {
    return this.request('/provider-scorecards/preview', { method: 'POST', body: metrics });
  }

  recalculateProviderScores() {
    return this.request('/provider-scorecards/recalculate', { method: 'POST' });
  }

  recordProviderCycle(id, body) {
    return this.request(`/provider-scorecards/${id}/cycles`, { method: 'POST', body });
  }

  setProviderProbation(id, onProbation, reason) {
    return this.request(`/provider-scorecards/${id}/probation`, { method: 'POST', body: { onProbation, ...(reason ? { reason } : {}) } });
  }

  async exportProviderScorecardsCsv(filters = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/provider-scorecards/export.csv?${this.scorecardParams(filters)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
      throw new Error(payload?.error?.message || `Export failed (${res.status})`);
    }
    return res.blob();
  }

  // ---- Compliance audit trail (GV-14; Auditor / tenant admins) ----
  auditParams(filters = {}) {
    return this.scorecardParams(filters);
  }

  getAuditLedger(filters = {}) {
    return this.request(`/compliance-audit/ledger?${this.auditParams(filters)}`);
  }

  getAuditBlock(id) {
    return this.request(`/compliance-audit/ledger/${id}`);
  }

  recalculateAuditBlock(id) {
    return this.request(`/compliance-audit/ledger/${id}/recalculate`, { method: 'POST' });
  }

  verifyAuditChain(filters = {}) {
    return this.request('/compliance-audit/verify', { method: 'POST', body: filters });
  }

  exportAuditLedger(body) {
    return this.request('/compliance-audit/export', { method: 'POST', body });
  }

  simulateAuditTamper(action) {
    return this.request('/compliance-audit/simulate-tamper', { method: 'POST', body: { action } });
  }

  // ---- System integrations: webhooks + global audit logs (Admin) ----
  getWebhookEndpoints() {
    return this.request('/admin/webhooks/endpoints');
  }

  createWebhookEndpoint(body) {
    return this.request('/admin/webhooks/endpoints', { method: 'POST', body });
  }

  setWebhookStatus(id, status) {
    return this.request(`/admin/webhooks/endpoints/${id}/status`, { method: 'PATCH', body: { status } });
  }

  revealWebhookSecret(id) {
    return this.request(`/admin/webhooks/endpoints/${id}/secret`);
  }

  pingWebhook(id) {
    return this.request(`/admin/webhooks/endpoints/${id}/ping`, { method: 'POST' });
  }

  rotateWebhookSecrets() {
    return this.request('/admin/webhooks/rotate-secrets', { method: 'POST' });
  }

  getWebhookDeliveries(filters = {}) {
    return this.request(`/admin/webhooks/deliveries?${this.scorecardParams(filters)}`);
  }

  getWebhookDelivery(id) {
    return this.request(`/admin/webhooks/deliveries/${id}`);
  }

  retryWebhookDelivery(id) {
    return this.request(`/admin/webhooks/deliveries/${id}/retry`, { method: 'POST' });
  }

  getSystemAuditLogs(filters = {}) {
    return this.request(`/admin/audit-logs?${this.scorecardParams(filters)}`);
  }

  // ---- Properties & plans: per-property billing (Finance / Customer Admin) ----
  getSubscription() {
    return this.request('/billing/subscription');
  }

  /** Confirm or change one property's plan (Customer Admin only). `body` is { planTier }. */
  setPropertyPlan(locationId, body) {
    return this.request(`/billing/subscription/properties/${locationId}/plan`, { method: 'PATCH', body });
  }

  /** `body` is { taxId?, address? }; an empty string clears the field. */
  updateBillingDetails(body) {
    return this.request('/billing/subscription/billing-details', { method: 'PATCH', body });
  }

  // ---- Invoicing & Stripe billing portal (Finance / Customer Admin) ----
  getBillingOverview() {
    return this.request('/billing/invoicing/overview');
  }

  getInvoices(filters = {}) {
    return this.request(`/billing/invoicing/invoices?${this.scorecardParams(filters)}`);
  }

  getInvoice(id) {
    return this.request(`/billing/invoicing/invoices/${id}`);
  }

  generateInvoice(body = {}) {
    return this.request('/billing/invoicing/invoices/generate', { method: 'POST', body });
  }

  retryInvoice(id) {
    return this.request(`/billing/invoicing/invoices/${id}/retry`, { method: 'POST' });
  }

  simulateStripeWebhook(id, body) {
    return this.request(`/billing/invoicing/invoices/${id}/simulate-webhook`, { method: 'POST', body });
  }

  addPaymentMethod(body) {
    return this.request('/billing/invoicing/payment-methods', { method: 'POST', body });
  }

  makeDefaultPaymentMethod(id) {
    return this.request(`/billing/invoicing/payment-methods/${id}/default`, { method: 'POST' });
  }

  removePaymentMethod(id) {
    return this.request(`/billing/invoicing/payment-methods/${id}`, { method: 'DELETE' });
  }

  createBillingPortalSession() {
    return this.request('/billing/invoicing/portal-session', { method: 'POST' });
  }

  async downloadBillingBlob(path) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
      throw new Error(payload?.error?.message || `Download failed (${res.status})`);
    }
    return res.blob();
  }

  exportInvoicesCsv(status = 'ALL') {
    return this.downloadBillingBlob(`/billing/invoicing/invoices/export?status=${encodeURIComponent(status)}`);
  }

  downloadInvoiceReceipt(id) {
    return this.downloadBillingBlob(`/billing/invoicing/invoices/${id}/receipt`);
  }

  // ---- Tenant configuration & custom branding ----
  getTenantBranding() {
    return this.request('/tenant-config/branding');
  }

  getTenantConfig() {
    return this.request('/tenant-config');
  }

  updateTenantConfig(body) {
    return this.request('/tenant-config', { method: 'PUT', body });
  }

  verifyTenantDomain() {
    return this.request('/tenant-config/domain/verify', { method: 'POST' });
  }

  testTenantTemplate(event, template) {
    return this.request(`/tenant-config/templates/${encodeURIComponent(event)}/test`, { method: 'POST', body: template ? { template } : {} });
  }

  /** Uploads the tenant logo / favicon as the raw image body. */
  async uploadTenantAsset(kind, file) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/tenant-config/assets/${kind}`, {
      method: 'PUT',
      headers: { 'Content-Type': file.type, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
    if (!res.ok) {
      const error = new Error(payload?.error?.message || `Upload failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return payload?.data;
  }

  async fetchTenantAssetBlob(kind) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/tenant-config/assets/${kind}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Asset unavailable (${res.status})`);
    return res.blob();
  }

  async fetchExecutionPhotoBlob(contentUrl) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${contentUrl}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Photo unavailable (${res.status})`);
    return res.blob();
  }

  // ---- Xamun Operator console (/admin): customers, property plans, billing terms, invoices and the plan catalogue ----
  getOperatorOverview() {
    return this.request('/operator/overview');
  }

  listOperatorCustomers({ q = '', status = 'ALL' } = {}) {
    const params = new URLSearchParams({ status });
    if (q) params.set('q', q);
    return this.request(`/operator/customers?${params}`);
  }

  getOperatorCustomer(id) {
    return this.request(`/operator/customers/${id}`);
  }

  onboardOperatorCustomer(body) {
    return this.request('/operator/customers', { method: 'POST', body });
  }

  suspendOperatorCustomer(id, reason) {
    return this.request(`/operator/customers/${id}/suspend`, { method: 'POST', body: { reason } });
  }

  reinstateOperatorCustomer(id, reason) {
    return this.request(`/operator/customers/${id}/reinstate`, { method: 'POST', body: { reason } });
  }

  addOperatorProperty(customerId, body) {
    return this.request(`/operator/customers/${customerId}/properties`, { method: 'POST', body });
  }

  setOperatorPropertyPlan(customerId, locationId, body) {
    return this.request(`/operator/customers/${customerId}/properties/${locationId}/plan`, { method: 'PATCH', body });
  }

  updateOperatorBilling(customerId, body) {
    return this.request(`/operator/customers/${customerId}/billing`, { method: 'PATCH', body });
  }

  pauseOperatorDunning(id, body) {
    return this.request(`/operator/customers/${id}/dunning/pause`, { method: 'POST', body });
  }

  resumeOperatorDunning(id, reason) {
    return this.request(`/operator/customers/${id}/dunning/resume`, { method: 'POST', body: { reason } });
  }

  recordOperatorInvoicePayment(invoiceId, body) {
    return this.request(`/operator/invoices/${invoiceId}/record-payment`, { method: 'POST', body });
  }

  extendOperatorInvoice(invoiceId, body) {
    return this.request(`/operator/invoices/${invoiceId}/extend`, { method: 'POST', body });
  }

  writeOffOperatorInvoice(invoiceId, reason) {
    return this.request(`/operator/invoices/${invoiceId}/write-off`, { method: 'POST', body: { reason } });
  }

  getOperatorPlans() {
    return this.request('/operator/plans');
  }

  updateOperatorPlan(tier, body) {
    return this.request(`/operator/plans/${encodeURIComponent(tier)}`, { method: 'PATCH', body });
  }

  listOperatorActions(limit = 100) {
    return this.request(`/operator/actions?limit=${limit}`);
  }

  // ---- AssetOS connector (asset register, preventive maintenance, warranty) ----
  getAssetOsConnectors() {
    return this.request('/assetos/connectors');
  }

  pairAssetOs(body) {
    return this.request('/assetos/connectors', { method: 'POST', body });
  }

  setAssetOsInboundUrl(id, assetosInboundUrl) {
    return this.request(`/assetos/connectors/${id}`, { method: 'PATCH', body: { assetosInboundUrl } });
  }

  setAssetOsActive(id, active) {
    return this.request(`/assetos/connectors/${id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  }

  rotateAssetOsSecret(id) {
    return this.request(`/assetos/connectors/${id}/rotate-secret`, { method: 'POST' });
  }

  sendAssetOsSnapshot(id) {
    return this.request(`/assetos/connectors/${id}/snapshot`, { method: 'POST' });
  }

  getAssetOsEvents(id, params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)).toString();
    return this.request(`/assetos/connectors/${id}/events${qs ? `?${qs}` : ''}`);
  }

  retryAssetOsEvent(id, eventId) {
    return this.request(`/assetos/connectors/${id}/events/${eventId}/retry`, { method: 'POST' });
  }

  /** AssetOS asset / warranty / preventive details for tickets and work orders (only linked ones are returned). */
  getAssetOsInfo({ ticketIds = [], workOrderIds = [] }) {
    const qs = new URLSearchParams();
    if (ticketIds.length) qs.set('ticketIds', ticketIds.join(','));
    if (workOrderIds.length) qs.set('workOrderIds', workOrderIds.join(','));
    return this.request(`/assetos/asset-info?${qs}`);
  }
}

export default new ApiService();
